import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, where, getDocs, updateDoc, doc, getDoc } from 'firebase/firestore';
import { Check, AlertCircle, PenTool, ArrowRight } from 'lucide-react';
import { handleFirestoreError, OperationType } from '../firebase';
import { useUser } from '../context/UserContext';

export default function Waiver() {
  const { waiverId } = useParams<{ waiverId: string }>();
  const navigate = useNavigate();
  const { user } = useUser();
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState<any>(null);
  const [formData, setFormData] = useState({ name: '', signature: '', accepted: false });
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    async function fetchWaiver() {
      if (!waiverId) return;
      try {
        let type = 'bookings';
        let foundDoc = null;
        
        try {
            const bookingDoc = await getDoc(doc(db, 'bookings', waiverId));
            if (bookingDoc.exists()) {
                foundDoc = bookingDoc;
            }
        } catch (e: any) {
            // Log warning but continue checking other categories
            console.warn("Waiver: error reading bookings", e);
        }
        
        if (!foundDoc) {
          try {
              const userDoc = await getDoc(doc(db, 'users', waiverId));
              if (userDoc.exists()) {
                  type = 'users';
                  foundDoc = userDoc;
              }
          } catch(e) { }
        }

        if (!foundDoc) {
             try {
                 const pendingDoc = await getDoc(doc(db, 'pending_memberships', waiverId));
                 if (pendingDoc.exists()) {
                     type = 'pending_memberships';
                     foundDoc = pendingDoc;
                 }
             } catch(e) { }
        }
        
        if (foundDoc) {
          const docData = foundDoc.data();
          setBooking({ 
            id: foundDoc.id, 
            type,
            clientName: docData.clientName || docData.displayName || docData.name || docData.email || 'Member',
            serviceName: docData.serviceName || docData.membershipTier || docData.planName || 'Annual Membership',
            date: docData.date || new Date().toLocaleDateString(),
            time: docData.time || 'N/A',
            ...docData 
          });
        } else {
          setError('Waiver not found or invalid link.');
        }
      } catch (err) {
        try {
            handleFirestoreError(err, OperationType.GET, 'waiver');
        } catch(e) {}
        setError('Error fetching waiver. Please ensure you have a valid waiver link.');
      } finally {
        setLoading(false);
      }
    }
    fetchWaiver();
  }, [waiverId, user]);

  const handleSign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!booking) return;
    setSigning(true);
    try {
      const docRef = doc(db, booking.type, booking.id);
      await updateDoc(docRef, {
        waiverSigned: true,
        waiverSignature: formData.signature,
        waiverSignedAt: new Date().toISOString()
      });
      setBooking({ ...booking, waiverSigned: true });
    } catch (err) {
      handleFirestoreError(err, OperationType.WRITE, booking.type);
      setError('Failed to sign waiver.');
    } finally {
      setSigning(false);
    }
  };

  if (loading) return <div className="min-h-screen pt-32 flex items-center justify-center p-4">Loading...</div>;

  return (
    <div className="pt-32 pb-24 bg-ows-white min-h-screen">
      <Helmet>
        <title>Digital Waiver | Open Water Syndicate</title>
      </Helmet>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 text-ows-teal mb-6">
            <PenTool className="h-6 w-6" />
            <span className="text-xs font-bold tracking-[0.5em] uppercase font-mono">Digital Waiver</span>
          </div>
          <h1 className="text-4xl font-display font-light uppercase tracking-widest text-ows-dark mb-6">
            Release of Liability
          </h1>
        </div>

        {error ? (
          <div className="p-6 bg-red-50 text-red-500 rounded-3xl text-center border border-red-100 flex items-center justify-center gap-2 text-sm uppercase tracking-widest font-bold">
            <AlertCircle className="h-5 w-5" /> {error}
          </div>
        ) : booking && booking.waiverSigned ? (
          <div className="p-12 bg-ows-teal/10 rounded-[3rem] text-center border border-ows-teal/20">
            <Check className="h-12 w-12 text-ows-teal mx-auto mb-6" />
            <h2 className="text-2xl font-display uppercase tracking-widest text-ows-dark mb-4">Waiver Signed</h2>
            <p className="text-ows-dark/60 font-light max-w-lg mx-auto">
              Your waiver has been successfully recorded for {booking.serviceName} on {booking.date}. You are cleared for launch.
            </p>
            <button onClick={() => navigate('/')} className="mt-8 px-8 py-4 bg-ows-dark text-white rounded-full font-bold uppercase tracking-widest text-xs hover:bg-ows-teal transition-all">
              Return Home
            </button>
          </div>
        ) : booking ? (
          <form onSubmit={handleSign} className="space-y-12">
            <div className="p-10 bg-white rounded-[3rem] border border-ows-teal/10 shadow-2xl space-y-6">
              <h3 className="text-lg font-bold uppercase tracking-widest text-ows-dark mb-4">Booking Details</h3>
              <div className="grid grid-cols-2 gap-4 text-sm bg-ows-grey p-6 rounded-2xl">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-ows-dark/40 font-bold mb-1">Service</p>
                  <p className="font-medium text-ows-dark">{booking.serviceName}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-ows-dark/40 font-bold mb-1">Date</p>
                  <p className="font-medium text-ows-dark">{booking.date} at {booking.time}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-[10px] uppercase tracking-widest text-ows-dark/40 font-bold mb-1">Participant</p>
                  <p className="font-medium text-ows-dark">{booking.clientName}</p>
                </div>
              </div>

              <div className="prose-ows mt-8 space-y-4 text-xs text-ows-dark/60 leading-relaxed h-64 overflow-y-auto p-6 bg-ows-grey/50 rounded-2xl border border-ows-teal/5">
                <p>I hereby acknowledge that paddleboarding involves inherent risks, including but not limited to: water hazards, changing weather conditions, equipment failure, and drowning.</p>
                <p>I certify that I am in good physical health and possess the necessary skills to participate in this activity.</p>
                <p>I agree to follow all safety instructions provided by Open Water Syndicate guides and personnel.</p>
                <p>I fully release and discharge Open Water Syndicate, its founders, guides, and affiliates from any and all liability, claims, or demands for personal injury, property damage, or wrongful death occurring during the session.</p>
                <p>I understand that I am responsible for my own safety and the safety of any minors I am signing for.</p>
              </div>
            </div>

            <div className="p-10 bg-ows-sand/20 rounded-[3rem] border border-ows-teal/10 space-y-6">
              <h3 className="text-lg font-bold uppercase tracking-widest text-ows-dark mb-4">Signature</h3>
              
              <label className="flex items-start gap-4 p-6 bg-white rounded-2xl border border-ows-teal/10 cursor-pointer hover:border-ows-teal/30 transition-all cursor-pointer">
                <input 
                  type="checkbox" 
                  className="mt-1"
                  required
                  checked={formData.accepted}
                  onChange={(e) => setFormData({...formData, accepted: e.target.checked})}
                />
                <span className="text-xs text-ows-dark leading-relaxed">
                  I have read this Release of Liability, fully understand its terms, and understand that I am giving up substantial rights, including my right to sue. I acknowledge that I am signing the agreement freely and voluntarily.
                </span>
              </label>

              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest text-ows-dark/40 ml-2">Type Full Legal Name as Signature</label>
                <input 
                  type="text" 
                  placeholder="John Doe" 
                  required 
                  value={formData.signature}
                  className="w-full px-6 py-4 bg-white border border-ows-teal/10 rounded-2xl text-ows-dark outline-none focus:border-ows-teal transition-colors font-serif italic text-lg" 
                  onChange={(e) => setFormData({...formData, signature: e.target.value})}
                />
              </div>

              <button 
                type="submit" 
                disabled={signing || !formData.accepted || !formData.signature}
                className="w-full py-5 bg-ows-dark text-white font-bold tracking-[0.2em] uppercase text-xs hover:bg-ows-teal transition-all rounded-full flex items-center justify-center gap-3 disabled:opacity-50 mt-4"
              >
                {signing ? 'Signing...' : 'Sign Legal Waiver'} <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </form>
        ) : null}
      </div>
    </div>
  );
}
